import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaServer, FaHeartbeat, FaBook, FaRocket, FaDatabase, FaWrench, FaGlobe, FaRegCopy, FaCheck, FaClone, FaEye, FaShieldAlt } from 'react-icons/fa';
import '../styles/McpPage.scss';

const MCP_COMMAND = 'claude mcp add --transport http --scope user redu https://mcp.redu.cloud/mcp --client-id redu-mcp';
const CODEX_COMMAND = 'codex mcp add redu --url https://mcp.redu.cloud/mcp --oauth-client-id redu-mcp';

// Pasted into the agent the tab names, it adds the MCP itself; one prompt per agent, so each stays short (Milos
// 2026-10-26). Checked 2026-10-26 in a throwaway config: Claude Code's command adds the server, which then needs the
// browser sign-in; Codex's command (codex-cli 0.142.5) adds it and starts the sign-in itself, printing a link and waiting.
export const CLAUDE_PROMPT = `Add the redu.cloud MCP server so you can manage my redu cloud. Run:
${MCP_COMMAND}
Then check it with claude mcp list and tell me what is left: restart Claude Code and sign in to my redu.cloud account in the browser (if it does not open, run /mcp and choose redu).`;
export const CODEX_PROMPT = `Add the redu.cloud MCP server so you can manage my redu cloud. Run:
${CODEX_COMMAND}
It prints a sign-in link and waits: show me the link so I can sign in to my redu.cloud account in the browser. Then check it with codex mcp list and tell me what is left.`;

// What to ask once it is connected: one short prompt per kind of cloud operation, each one a thing an agent does with
// the MCP's tools (deploy_app, create_database, upgrade_to_cluster, SSH and logs, verify_domain, create_instance). The
// last three are Milos's (2026-10-26). A click copies the prompt.
export const OPERATIONS: { icon: React.ElementType; title: string; prompt: string }[] = [
    { icon: FaRocket, title: 'Ship an app', prompt: 'Deploy this project to redu.' },
    { icon: FaDatabase, title: 'Add a database', prompt: 'Give this app a Postgres database.' },
    { icon: FaHeartbeat, title: 'Keep it running', prompt: 'Make this app highly available.' },
    { icon: FaWrench, title: 'Fix an incident', prompt: 'web-1 returns 502s. Find out why and fix it.' },
    { icon: FaGlobe, title: 'Use your domain', prompt: 'Serve this app on shop.example.com.' },
    { icon: FaServer, title: 'Get a machine', prompt: 'Give me an Ubuntu VM I can SSH into.' },
    { icon: FaClone, title: 'Make a replica', prompt: 'Create a replica of my entire cloud setup for testing.' },
    { icon: FaEye, title: 'Watch your VMs', prompt: 'Add a worker to watch my other VMs.' },
    { icon: FaShieldAlt, title: 'Harden a VM', prompt: 'Harden my VM with the latest security patches.' },
];

// The MCP page (/mcp): the former AI page, now the one page for connecting an agent (Milos 2026-10-26: the AI and MCP
// pages did the same job; /ai redirects here).
const McpPage: React.FC = () => {
    const [copied, setCopied] = useState(false);
    const [promptCopied, setPromptCopied] = useState(false);
    const [opCopied, setOpCopied] = useState<number | null>(null);
    const [client, setClient] = useState<'claude' | 'codex'>('claude');
    const command = client === 'codex' ? CODEX_COMMAND : MCP_COMMAND;

    const copyText = (text: string, done: () => void) => {
        if (typeof navigator !== 'undefined' && navigator.clipboard) {
            navigator.clipboard.writeText(text).then(done);
        }
    };
    const flash = (set: (v: boolean) => void) => () => { set(true); setTimeout(() => set(false), 2000); };
    const copyCommand = () => copyText(command, flash(setCopied));
    const copyPrompt = () => copyText(client === 'codex' ? CODEX_PROMPT : CLAUDE_PROMPT, flash(setPromptCopied));
    const copyOperation = (i: number) => copyText(OPERATIONS[i].prompt, () => {
        setOpCopied(i);
        setTimeout(() => setOpCopied((cur) => (cur === i ? null : cur)), 2000);
    });

    return (
        <div className="ai-page">
            <div className="ai-container">

                <header className="ai-hero">
                    <span className="ai-kicker">redu MCP</span>
                    <h1>Your agent runs your cloud.</h1>
                    <p className="ai-sub">Connect it once, then ask in plain words. Real EU-hosted machines, at agent speed.</p>
                </header>

                <section className="ai-step" aria-labelledby="ai-step-connect">
                    <h2 id="ai-step-connect"><span className="ai-step-num">1</span> Connect your agent</h2>
                    <div className="ai-panel">
                        <div className="ai-tabs" role="group" aria-label="Agent">
                            {(['claude', 'codex'] as const).map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    className={`ai-tab ${client === c ? 'ai-tab-active' : ''}`}
                                    aria-pressed={client === c}
                                    onClick={() => setClient(c)}
                                >
                                    {c === 'claude' ? 'Claude Code' : 'Codex'}
                                </button>
                            ))}
                        </div>
                        <code className="ai-cmd">{command}</code>
                        <div className="ai-actions">
                            <button className="ai-copy-btn" type="button" onClick={copyCommand}>
                                {copied ? <><FaCheck /> Copied</> : <><FaRegCopy /> Copy command</>}
                            </button>
                            <button
                                className="ai-copy-btn ai-copy-btn-secondary"
                                type="button"
                                onClick={copyPrompt}
                                title={`A prompt to paste into ${client === 'codex' ? 'Codex' : 'Claude Code'}: it adds redu itself`}
                            >
                                {promptCopied ? <><FaCheck /> Copied</> : <><FaRegCopy /> Copy prompt</>}
                            </button>
                        </div>
                        <p className="ai-note">
                            You sign in with your redu.cloud account. For CI, use an{' '}
                            <Link to="/category/user/api-keys" className="ai-link">API key</Link>.
                        </p>
                    </div>
                </section>

                <section className="ai-step" aria-labelledby="ai-step-ask">
                    <h2 id="ai-step-ask"><span className="ai-step-num">2</span> Ask it to run your cloud</h2>
                    <div className="ai-ops">
                        {OPERATIONS.map((op, i) => (
                            <button
                                key={op.title}
                                type="button"
                                className="ai-op"
                                onClick={() => copyOperation(i)}
                                aria-label={`${op.title}: copy "${op.prompt}"`}
                            >
                                <span className="ai-op-head">
                                    <op.icon className="ai-op-icon" aria-hidden="true" />
                                    <span className="ai-op-title">{op.title}</span>
                                    <span className="ai-op-copy" aria-hidden="true">{opCopied === i ? <FaCheck /> : <FaRegCopy />}</span>
                                </span>
                                <span className="ai-op-prompt">{opCopied === i ? 'Copied' : `"${op.prompt}"`}</span>
                            </button>
                        ))}
                    </div>
                </section>

                <footer className="ai-foot">
                    <span>Real machines with full SSH, not a sandbox.</span>
                    <a href="https://docs.redu.cloud" target="_blank" rel="noopener noreferrer" className="ai-foot-link">
                        <FaBook /> Docs
                    </a>
                </footer>

            </div>
        </div>
    );
};

export default McpPage;
